/* global confirm */
import { showResponseError, showSuccess } from 'utils/messages';
import { scrollTop, getFileReader, downloadStringAsFile, errorIfNoFileReader } from 'utils/wrappers';

import FulfillmentOrders from 'app/collections/fulfillment_orders';
import FulfillmentOrder from 'app/models/fulfillment_order';
import FulfillmentOrderReport from 'app/models/fulfillment_order_report';
import Paginator from 'app/models/paginator';
import DateRange, { DATERANGE_PARAM_TYPES } from 'app/models/date_range';

import Base from 'app/views/base';
import DatepickerRangeView from 'app/views/datepicker_range_view';
import PaginationView from 'app/views/pagination_view';
import RequestReportView from 'app/views/request_report_view';
import SelectorView from 'app/views/selector_view';

import { List as ListView, Heading } from 'app/components/list/list';
import ListSearchView from 'app/components/list/search';
import { DetailBlock } from 'app/components/list/object_details';
import PageHeaderView from 'app/components/page_header';

import tmpl from './template';
import uploadOrdersTemplate from './upload_orders';

const STATUS_MAP = {
  created: 'Created',
  preparing: 'Preparing Shipment',
  shipped: 'Shipped',
};

const PAGE_SIZE = 10;

export const UploadOrdersView = Base.extend({
  compiledTemplate: uploadOrdersTemplate,

  events: { 'change input.read-file': 'uploadOrders' },

  initialize({ apiModeSelection }) {
    this.apiModeSelection = apiModeSelection;
    this.uploading = false;
  },

  context() {
    return {
      uploading: this.uploading,
    };
  },

  uploadOrders({ target }) {
    const input = target;

    errorIfNoFileReader(input);

    const file = input.files[0];
    this.readFile(file, (data) => {
      const self = this;
      self.uploading = true;
      self.render();

      new FulfillmentOrder().uploadOrdersCSV({
        data,
        apiModeSelection: this.apiModeSelection,
        success(responseData, textStatus, jqXHR) {
          const filename = jqXHR.getResponseHeader('content-disposition').split('=')[1];
          self.collection.fetch({ reset: true });
          downloadStringAsFile(responseData, filename);
        },
        error(jqXHR) {
          showResponseError(jqXHR);
        },
        complete() {
          self.uploading = false;
          self.render();
        },
      });
    });
  },

  readFile (file, cb) {
    const reader = getFileReader();
    reader.onload = (event) => {
      const csvData = event.target.result;
      cb(csvData);
    };
    reader.readAsText(file);
  },
});

export default Base.extend({
  compiledTemplate: tmpl,
  className: 'fulfillment_orders',

  initialize({ apiModeSelection }) {
    this.apiModeSelection = apiModeSelection;
    this.dateRange = new DateRange({ paramType: DATERANGE_PARAM_TYPES.DATETIME });
    this.collection = new FulfillmentOrders([], { dateRange: this.dateRange, apiModeSelection: this.apiModeSelection });
    this.paginator = new Paginator({
      collection: this.collection,
      pageSize: PAGE_SIZE,
      buildParams() {
        return {
          limit: PAGE_SIZE,
          page: this.get('currentPage'),
          offset: this.get('currentPage') * PAGE_SIZE,
        };
      }
    });

    this.listenTo(this.collection, 'sync', scrollTop);
    this.listenTo(this.collection, 'update', this.render);

    const headings = [
      new Heading({
        property: 'id',
        display: 'Id',
        xsSize: 6,
        smSize: 6,
      }),
      new Heading({
        text: m => STATUS_MAP[m.get('status')],
        display: 'Status',
        xsSize: 2,
        smSize: 2,
        wrapperClass: s => s ? `order-status__${s.get('status')}` : '',
      }),
      new Heading({
        display: '',
        property: 'status',
        text: 'Cancel',
        onClick: this.cancelOrder.bind(this),
        wrapperClass: s => (
          s ? 'order-cancel-button' + (s.get('status') === 'created' ? '' : ' order-cancel-button__hidden') : ''
        ),
        xsSize: 2,
        smSize: 2
      }),
    ];

    this.addSubview('header', 'header', new PageHeaderView({
      name: 'Fulfillment Orders',
    }));
    this.addSubview('orderList', '#order_list', new ListView({
      headings,
      collection: this.collection,
      user: this.user,
      noResultsMessage: 'No orders found matching your filter set.',
      showDetails: {
        mainHeading: m => m.get('id'),
        detailBlocks: [
          new DetailBlock({
            heading: 'Status',
            items:  m => [ m.get('status') ],
          }),
          new DetailBlock({
            heading: 'Destination',
            items: m => m.addressArray(),
          }),
          new DetailBlock({
            heading: 'Phone',
            items: m => [ m.get('destination').phone ],
          }),
          new DetailBlock({
            heading: 'Tracking Codes',
            items: m => {
              return m.get('tracking_codes').map(t => t);
              // this will require an api call to get the real tracking url, TODO fix later
              //t => `<a href="https://track.easypost.com/${t}" target="_blank">${t}</a>`).join('<br />'),
            },
          }),
        ],
      },
    }));

    this.addSubview('pagination', '#order_pagination', new PaginationView({ model: this.paginator }));
    this.addSubview('apiModeSelector', '#api_mode_selector', new SelectorView({ model: this.apiModeSelection }));
    this.addSubview('datepickers', '#order_daterange_pickers', new DatepickerRangeView({ model: this.dateRange }));

    this.addSubview('requestReport', '#request_report', new RequestReportView({
      collection: this.collection,
      ReportClass: FulfillmentOrderReport,
      dateRange: this.dateRange,
      apiModeSelection: this.apiModeSelection,
    }));

    this.addSubview('uploadOrders', '#upload_orders', new UploadOrdersView({
      collection: this.collection,
      apiModeSelection: this.apiModeSelection,
    }));

    this.addSubview('search', '#order_search', new ListSearchView({
      collection: this.collection,
      placeholder: '(Order Id, Tracking Code, or Name)',
    }));
  },

  cancelOrder(order) {
    const id = order.get('id');

    if (order.get('status') !== 'created') { return; }

    if (confirm(`Are you sure you want to delete order #${id}? To retrieve it, you will need to contact customer service.`)) {
      order.destroy({
        data: { mode: order.get('mode') },
        emulateJSON: true,
        wait: true,
        success() {
          showSuccess(`Order #${id} deleted.`);
        },
        failure(model, res) {
          showResponseError(res);
        },
      });
    }
  },
});



// WEBPACK FOOTER //
// ./app/components/pages/fulfillment_orders/index.js